import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Breadcrumbs,
  Link as MUILink,
  Divider,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  getCart,
  updateCart,
  removeCartItem,
  updateCartItemLocally,
} from "../redux/slices/cartSlice";
import { getPaymentOptionByBuyer } from "../redux/slices/paymentOptionSlice";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { createOrder } from "../redux/slices/ordersSlice";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import PageTitle from "../Components/PageTitle";

export default function CartPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const { userId, sellerIds = [], buyerCategories = [] } = location.state || {};
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { cart, loading, error } = useSelector((state) => state.cart);

  const { user } = useSelector((state) => state.user);
  const { items: paymentOptions } = useSelector(
    (state) => state.paymentOptions,
  );

  const [payment, setPayment] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [updatingItems, setUpdatingItems] = useState(new Set()); // Track updating items

  // 🔹 Fetch cart
  useEffect(() => {
    if (user?._id && sellerIds.length > 0) {
      dispatch(
        getCart({ userId: user._id, seller: sellerIds, buyerCategories }),
      );
    }
  }, [dispatch, user?._id, sellerIds, buyerCategories]);

  // 🔹 Fetch payment options
  useEffect(() => {
    if (buyerCategories?.[0] && sellerIds?.[0]) {
      dispatch(
        getPaymentOptionByBuyer({
          buyerCategory: buyerCategories[0],
          seller: sellerIds[0],
        }),
      );
    }
  }, [dispatch, buyerCategories, sellerIds]);

  // 🔹 Set payment type
  useEffect(() => {
    if (paymentOptions[0]?.paymentType === "Cash") {
      setPayment("cash");
      setDiscountPercent(paymentOptions[0]?.cashPayment?.discountPercent || 0);
    } else if (paymentOptions[0]?.paymentType === "Credit") {
      setPayment("credit");
      setDiscountPercent(0);
    } else if (paymentOptions[0]?.paymentType === "Both") {
      // Default to cash if both are allowed
      setPayment("cash");
      setDiscountPercent(paymentOptions[0]?.cashPayment?.discountPercent || 0);
    }
  }, [paymentOptions]);

  // Safe cart items extraction with better product data handling
  const cartItems = React.useMemo(() => {
    if (!cart) return [];

    let items = [];

    if (Array.isArray(cart)) {
      // If cart is array of items
      items = cart;
    } else if (Array.isArray(cart.cart)) {
      // If cart has cart array
      items = cart.cart;
    } else if (Array.isArray(cart.data)) {
      // If cart has data array
      items = cart.data;
    } else if (cart.items && Array.isArray(cart.items)) {
      // If cart has items array
      items = cart.items;
    } else if (Array.isArray(cart)) {
      // If it's already an array of carts with items
      items = cart.flatMap((cart) => cart.items || []);
    }

    return items;
  }, [cart]);

  // Get complete product data from item
  const getProductData = (item) => {
    // If item has full product object
    if (item.product && typeof item.product === "object" && item.product.name) {
      return item.product;
    }

    // If product is just an ID, try to find it in all cart items
    if (typeof item.product === "string" || item.productId) {
      const productId = item.product || item.productId;
      const itemWithProduct = cartItems.find(
        (cartItem) =>
          (cartItem.product?._id === productId ||
            cartItem.product === productId) &&
          cartItem.product &&
          typeof cartItem.product === "object" &&
          cartItem.product.name,
      );

      return (
        itemWithProduct?.product || { name: "Product", price: item.mrp || 0 }
      );
    }

    return { name: "Product", price: item.mrp || item.finalPrice || 0 };
  };

  // Calculate product price with fallbacks
  const getProductPrice = (item) => {
    const product = getProductData(item);

    const price =
      product?.productVisibility?.[0]?.price ||
      product?.price ||
      product?.mrp ||
      item?.mrp ||
      item?.finalPrice ||
      item?.discountPrice ||
      0;

    return Number(price);
  };

  // Calculate GST with fallbacks
  const getGstPercent = (item) => {
    const product = getProductData(item);

    const gst = product?.category?.gst || product?.gst || item?.gstPercent || 0;

    return Number(gst);
  };

  // Get product name with fallbacks
  const getProductName = (item) => {
    const product = getProductData(item);
    return product?.name || item?.productName || "Product";
  };

  // Get product ID for operations
  const getProductId = (item) => {
    return item.product?._id || item.product || item.productId;
  };

  // Get seller ID for operations
  const getSellerId = (item) => {
    return item.product?.user?._id || item.seller || sellerIds[0];
  };

  const handleQuantityChange = async (item, newQty) => {
    if (newQty < 1) return;

    const productId = getProductId(item);
    const seller = getSellerId(item);

    if (!productId) {
      toast.error("Product ID not found");
      return;
    }

    // Optimistic update - update UI immediately
    setUpdatingItems((prev) => new Set(prev).add(productId));
    dispatch(updateCartItemLocally({ productId, quantity: newQty }));

    try {
      const result = await dispatch(
        updateCart({ userId: user?._id, productId, quantity: newQty, seller }),
      ).unwrap();

      if (result.success) {
        toast.success("Cart updated successfully!");

        // Refresh cart to get complete updated data
        setTimeout(() => {
          dispatch(
            getCart({ userId: user._id, seller: sellerIds, buyerCategories }),
          );
        }, 500);
      }
    } catch (error) {
      toast.error(error.message || "Failed to update cart");

      // Revert optimistic update on error
      dispatch(
        getCart({ userId: user._id, seller: sellerIds, buyerCategories }),
      );
    } finally {
      setUpdatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  // CartPage component mein handleRemove function update karein
  const handleRemove = async (item) => {
    try {
      const itemId = item._id;
      const productId = getProductId(item);

      const result = await dispatch(
        removeCartItem({
          userId: user?._id,
          itemId: itemId,
          productId: productId,
        }),
      ).unwrap();

      if (result.success) {
        toast.success("Item removed from cart!");

        // Refresh cart data
        dispatch(
          getCart({ userId: user._id, seller: sellerIds, buyerCategories }),
        );
      }
    } catch (error) {
      toast.error(error.message || "Failed to remove item");
    }
  };

  // Loading state
  if (loading && cartItems.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="70vh"
      >
        <Typography variant="h6">Loading your cart...</Typography>
      </Box>
    );
  }

  // Empty state
  if (!loading && cartItems.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h5" color="text.secondary">
          Your cart is empty 🛒
        </Typography>
        <Button
          variant="contained"
          component={Link}
          to="/buyer-dashboard/seller-network"
          sx={{
            background: "#0b4064",
            "&:hover": {
              background: "#09324d",
            },
          }}
        >
          Back To Seller Network
          {/* Continue Shopping */}
        </Button>
      </Box>
    );
  }

  // Calculate totals with proper price extraction
  const total = cartItems.reduce((sum, item) => {
    const basePrice = getProductPrice(item);
    const gstPercent = getGstPercent(item);
    const gstAmount = (basePrice * gstPercent) / 100;
    const priceWithGst = basePrice + gstAmount;
    const itemTotal = priceWithGst * item.quantity;

    return sum + itemTotal;
  }, 0);

  const discount =
    payment === "cash" ? Math.floor((total * discountPercent) / 100) : 0;
  const grandTotal = total - discount;

  const handleSendEnquiry = async () => {
    try {
      const seller = sellerIds?.[0];
      await dispatch(
        createOrder({
          seller,
          selectPaymentType: payment === "cash" ? "Cash" : "Credit",
        }),
      ).unwrap();

      // Optional: refresh cart
      dispatch(
        getCart({ userId: user._id, seller: sellerIds, buyerCategories }),
      );

      toast.success("Enquiry / Purchase Order sent successfully!");

      // Redirect to orders page after successful order creation
      setTimeout(() => {
        navigate("/buyer-dashboard/my-orders");
      }, 1500);
    } catch (err) {
      toast.error(err.message || "Failed to send enquiry / purchase order");
    }
  };

  return (
    <>
      <PageTitle
        title="Your Cart - BizBridge"
        description="Review products added to your cart from the selected seller. Update quantities, choose payment type, view GST and discounts, and send enquiry or purchase order easily."
        keywords="buyer cart, seller cart, B2B cart, wholesale cart, review order, payment type, GST invoice, purchase order, BizBridge cart, seller wise cart"
        url="/buyer-dashboard/cart"
      />

      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "#f8fafc",
          py: { xs: 1, sm: 2 },
        }}
      >
        <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
          {/* Breadcrumb */}
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
              width: "fit-content",
              backgroundColor: "#fff",
              padding: "13px",
              borderRadius: "12px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            }}
          >
            <MUILink
              component={Link}
              to="/buyer-dashboard/view-products"
              sx={{ color: "inherit", textDecoration: "none" }}
            >
              All Products
            </MUILink>
            <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
              Cart
            </Typography>
          </Breadcrumbs>

          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              minHeight: "100vh",
              px: { xs: 2, sm: 3 },
              pt: 2,
              backgroundColor: "#fff",
              borderRadius: 2,
              boxShadow: "0 2px 4px 0 rgba(36,50,93,0.09)",
            }}
          >
            <Typography fontWeight={700} fontSize={22} sx={{ mb: 3 }}>
              Cart
            </Typography>

            {/* Payment Type Selection */}

            <Box mb={3}>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                mb={1}
              >
                <Typography sx={{ color: "#0b4064", fontWeight: 700, mr: 1 }}>
                  Payment Type:
                </Typography>
 
                <ToggleButtonGroup
                  value={payment}
                  exclusive
                  onChange={(e, newValue) => {
                    if (
                      newValue &&
                      paymentOptions?.[0]?.paymentType === "Both"
                    ) {
                      setPayment(newValue);
                      if (newValue === "cash") {
                        setDiscountPercent(
                          paymentOptions[0]?.cashPayment?.discountPercent || 0,
                        );
                      } else {
                        setDiscountPercent(0);
                      }
                    }
                  }}
                  disabled={paymentOptions?.[0]?.paymentType !== "Both"}
                  size="small"
                >
                  <ToggleButton value="cash">Cash</ToggleButton>
                  <ToggleButton value="credit">Credit</ToggleButton>
                </ToggleButtonGroup>

                {paymentOptions?.[0]?.paymentType !== "Both" && (
                  <Typography variant="caption" color="textSecondary">
                    (Fixed by seller: {paymentOptions?.[0]?.paymentType})
                  </Typography>
                )}
              </Box>

              {payment === "cash" && discountPercent > 0 && (
                <Typography variant="body2" color="green">
                  You will get {discountPercent}% discount on cash payment
                </Typography>
              )}
            </Box>

            {/* Cart Table */}
            <TableContainer
              component={Paper}
              sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#0b4064" }}>
                      Product
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#0b4064" }}>
                      Price
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#0b4064" }}>
                      Qty
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#0b4064" }}>
                      Total (w/ GST)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#0b4064" }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartItems.map((item) => {
                    const basePrice = getProductPrice(item);
                    const gstPercent = getGstPercent(item);
                    const gstAmount = (basePrice * gstPercent) / 100;
                    const priceWithGst = basePrice + gstAmount;
                    const totalWithGst = priceWithGst * item.quantity;
                    const productName = getProductName(item);
                    const productId = getProductId(item);
                    const isUpdating = updatingItems.has(productId);

                    return (
                      <TableRow
                        key={item._id || productId}
                        sx={{
                          opacity: isUpdating ? 0.6 : 1,
                          "&:hover": { backgroundColor: "#fafafa" },
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight={600}>
                            {productName}
                          </Typography>
                          {getProductData(item)?.category?.name && (
                            <Typography variant="caption" color="textSecondary">
                              Category: {getProductData(item).category.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>
                            ₹{basePrice.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            (GST: {gstPercent}%)
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IconButton
                              size="small"
                              disabled={item.quantity <= 1 || isUpdating}
                              onClick={() =>
                                handleQuantityChange(item, item.quantity - 1)
                              }
                              sx={{
                                border: "1px solid #ddd",
                                "&:hover": { backgroundColor: "#f0f0f0" },
                              }}
                            >
                              <Remove fontSize="small" />
                            </IconButton>
                            <Typography
                              fontWeight={600}
                              minWidth="30px"
                              textAlign="center"
                              sx={{
                                padding: "8px 12px",
                                border: "1px solid #ddd",
                                borderRadius: 1,
                              }}
                            >
                              {isUpdating ? "..." : item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              disabled={isUpdating}
                              onClick={() =>
                                handleQuantityChange(item, item.quantity + 1)
                              }
                              sx={{
                                border: "1px solid #ddd",
                                "&:hover": { backgroundColor: "#f0f0f0" },
                              }}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600} color="#0b4064">
                            ₹{totalWithGst.toLocaleString()}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            (₹{priceWithGst.toLocaleString()} × {item.quantity})
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleRemove(item)}
                            disabled={loading || isUpdating}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "flex-start", lg: "center" }}
              mt={4}
              p={3}
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e0e0e0",
              }}
            >
              {/* Order Summary Section */}
              <Box
                sx={{
                  width: "100%",
                  mb: { xs: 2, md: 3, lg: 0 },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  gutterBottom
                  color="#0b4064"
                >
                  Order Summary
                </Typography>

                {/* Pricing Details - Now this will be above button on md screens */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mb: { xs: 0, md: 3, lg: 0 },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography>Subtotal:</Typography>
                    <Typography fontWeight={600}>
                      ₹{total.toLocaleString()}
                    </Typography>
                  </Box>

                  {discount > 0 && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography color="green">
                        Discount ({discountPercent}%):
                      </Typography>
                      <Typography fontWeight={600} color="green">
                        -₹{discount.toLocaleString()}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6" fontWeight={700}>
                      Grand Total:
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="#0b4064">
                      ₹{grandTotal.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Button - Below pricing on md, beside on lg */}
              <Box
                sx={{
                  width: "100%",
                  mt: { xs: 2, md: 0, lg: 0 },
                  display: "flex",
                  justifyContent: { xs: "flex-start", lg: "flex-end" },
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background:
                      "linear-gradient(135deg, #0b4064 0%, #09324d 100%)",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    px: { xs: 3, md: 4 },
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(11, 64, 100, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #09324d 0%, #07243c 100%)",
                      boxShadow: "0 6px 16px rgba(11, 64, 100, 0.4)",
                    },
                    width: { xs: "100%", md: "100%", lg: "auto" },
                  }}
                  onClick={handleSendEnquiry}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Send Enquiry / Purchase Order"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
